import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, IconButton } from '@nimbus-ds/components';
import { StopFilledIcon, ArrowupIcon } from '@nimbus-ds/icons';
import { ChatInput } from '@nimbus-ds/patterns';
import { useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useAgentChatPanelContext } from 'App/components/AgentChatPanelContext';
import { routesMerged } from 'App/constants/routesMerged';
import { useTranslationCommon } from 'commons/hooks';
import { logError } from 'commons/logger';
import { mapStatesToAgentChatPanelContext } from 'commons/services/AgentChatService';
import { useAgentChatQuery } from 'domains/Ai/hooks';
import { AgentChatInputProps } from './types';
import { useAgentChatContext } from '../../contexts';
import {
  trackAgentChatActionGenerated,
  trackAgentChatMessageSent,
} from '../../tracking';
import type { ChatMessage } from '../../types';
import { mapRoutesToNavigation } from '../../utils/mappings';

export function AgentChatInput({
  externalInputValue,
  setExternalInputValue,
}: Readonly<AgentChatInputProps>) {
  const { actionBus } = useAgentChatPanelContext();
  const [inputValue, setInputValue] = useState('');
  const t = useTranslationCommon('ai.agent');
  const {
    addUserMessage,
    addAiMessage,
    isLoading,
    setIsLoading,
    sessionId,
    lastRunId,
  } = useAgentChatContext();
  const {
    sendMessage,
    isLoading: queryIsLoading,
    data,
    error,
    abortRequest,
  } = useAgentChatQuery();
  const { pathname } = useLocation();

  const processedDataRef = useRef<string | null>(null);
  const processedErrorRef = useRef<unknown>(null);

  const errorText = t('error');

  useEffect(() => {
    if (externalInputValue) {
      setInputValue(externalInputValue);
      setExternalInputValue('');
    }
  }, [externalInputValue, setExternalInputValue, setInputValue]);

  useEffect(() => {
    setIsLoading(queryIsLoading);
  }, [queryIsLoading, setIsLoading]);

  useEffect(() => {
    if (data?.run_id && processedDataRef.current !== data.run_id) {
      if (data.agent_response?.response) {
        const actionChain = data.agent_response?.action_chain || [];
        const helpLinks = data.agent_response?.help_links || [];

        if (actionChain.length > 0) {
          trackAgentChatActionGenerated({
            runId: data.run_id || null,
            sessionId: data.session_id || null,
            actions: actionChain,
          });
        }

        const aiMessage: ChatMessage = {
          id: data.run_id,
          content: data.agent_response.response,
          isUser: false,
          timestamp: new Date(),
          actionChain,
          helpLinks,
          sessionId: data.session_id || null,
        };

        addAiMessage(aiMessage);
        processedDataRef.current = data.run_id;
      }
    }
  }, [data, addAiMessage, sessionId]);

  useEffect(() => {
    if (error && processedErrorRef.current !== error) {
      if (
        typeof error === 'object' &&
        'name' in error &&
        (error as Error).name === 'AbortError'
      ) {
        return;
      }

      const errorMessage: ChatMessage = {
        id: uuidv4(),
        content: errorText,
        isUser: false,
        timestamp: new Date(),
        actionChain: [],
      };
      addAiMessage(errorMessage);
      processedErrorRef.current = error;
    }
  }, [error, addAiMessage, errorText]);

  const navigationRoutes = useMemo(
    () => mapRoutesToNavigation(routesMerged),
    [],
  );

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = addUserMessage(inputValue.trim());
    setInputValue('');

    processedDataRef.current = null;
    processedErrorRef.current = null;

    const componentStates = actionBus?.getCurrentState();
    const { context, imageUrls } =
      mapStatesToAgentChatPanelContext(componentStates);

    trackAgentChatMessageSent({
      messageContent: userMessage.content,
      sessionId,
      runId: lastRunId,
      imageUrls,
      currentRoute: pathname,
      context,
    });

    try {
      await sendMessage({
        message: userMessage.content,
        sessionId,
        context,
        imageUrls,
        navigation: { current_route: pathname, routes: navigationRoutes },
      });
    } catch (err) {
      logError('Agent request failed', err, {
        sessionId: sessionId || undefined,
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleButtonClick = () => {
    if (isLoading) {
      abortRequest();
    } else {
      handleSendMessage();
    }
  };

  return (
    <Box p="4">
      <ChatInput
        aiFocused={!isLoading}
        borderColor={isLoading ? 'neutral-surfaceDisabled' : 'transparent'}
      >
        <ChatInput.Field
          id="agent-input"
          placeholder={isLoading ? '' : t('inputPlaceholder')}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <ChatInput.Actions>
          <Box
            display="flex"
            gap="2"
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
          >
            <IconButton
              appearance={isLoading ? undefined : 'ai-generative'}
              color={
                !inputValue.trim()
                  ? 'neutral-textHigh'
                  : isLoading
                  ? 'neutral-textHigh'
                  : 'neutral-surface'
              }
              source={isLoading ? <StopFilledIcon /> : <ArrowupIcon />}
              onClick={handleButtonClick}
              disabled={!isLoading && !inputValue.trim()}
              size="2rem"
            />
          </Box>
        </ChatInput.Actions>
      </ChatInput>
    </Box>
  );
}
